import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Row,
  Col,
  message,
  Tag,
  Divider,
  Radio,
  Tabs,
} from 'antd';
import {
  PhoneOutlined,
  ThunderboltOutlined,
  SendOutlined,
  ReloadOutlined,
  CopyOutlined,
  PlayCircleOutlined,
  StopOutlined,
  CloseCircleOutlined,
  CodeOutlined,
  UserOutlined,
  HistoryOutlined,
  LinkOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { callCenterFakeCallApi } from '../../utils/api';
import { useSocket, useSocketEvent } from '../../hooks/useSocket';
import { useAuth } from '../../contexts/AuthContext';
import { useCallCenterOptional } from '../../contexts/CallCenterContext';
import PermissionGate from '../../components/PermissionGate';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const DEFAULT_EVENT_OPTIONS = [
  { value: 'ringing', label: 'تماس ورودی (Ringing / Screen Pop)', desc: 'نمایش پاپ‌آپ تماس ورودی و بوق زنگ برای اپراتور' },
  { value: 'answered', label: 'پاسخ داده شد (Answered)', desc: 'آغاز مکالمه و فعال شدن تایمر مکالمه' },
  { value: 'talking', label: 'در حال مکالمه (Talking)', desc: 'مکالمه جاری و تغییر وضعیت اپراتور به مشغول' },
  { value: 'hangup', label: 'پایان تماس (Hangup)', desc: 'قطع تماس و باز شدن فرم ثبت نتیجه / بستن پاپ‌آپ' },
  { value: 'screen_pop', label: 'رویداد مستقیم اسکرین‌پاپ (Screen Pop)', desc: 'ارسال مستقیم رویداد باز کردن پاپ‌آپ مشتری' },
  { value: 'audio_ready', label: 'فایل صوتی آماده شد (Audio Ready)', desc: 'اطلاع‌رسانی در دسترس بودن فایل ضبط‌شده' },
  { value: 'custom', label: 'رویداد سفارشی... (Custom Event)', desc: 'وارد کردن نام رویداد دلخواه به صورت دستی' },
];

const CallCenterFakeCall = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { user } = useAuth();
  const callCenter = useCallCenterOptional();
  const { isConnected } = useSocket();

  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({
    agents: [],
    queues: [],
    sample_contacts: [],
    my_agent: null,
  });

  const [selectedEventType, setSelectedEventType] = useState('ringing');
  const [customEvent, setCustomEvent] = useState('');
  const [customJsonMode, setCustomJsonMode] = useState(false);
  const [customJsonText, setCustomJsonText] = useState('{}');

  // Simulation sequence state (Auto Flow: Ringing -> Answered -> Hangup)
  const [sequenceActive, setSequenceActive] = useState(false);
  const [sequenceStep, setSequenceStep] = useState(0);
  const sequenceRef = useRef({ active: false, callId: null, timerId: null });

  // History of published fake calls in this session
  const [publishHistory, setPublishHistory] = useState([]);

  // Live intercepted socket events on this page
  const [liveSocketLogs, setLiveSocketLogs] = useState([]);

  // Generate random IDs
  const generateRandomCallId = useCallback(() => Math.floor(1000 + Math.random() * 90000), []);

  // Load server options (agents, queues, sample contacts)
  const loadOptions = useCallback(async () => {
    try {
      const res = await callCenterFakeCallApi.getOptions();
      const data = res?.data || {};
      setMeta(data);

      // Pre-fill default values
      const initialCallId = generateRandomCallId();
      const myExt = callCenter?.myAgentExtension || data?.my_agent?.extension || '999';
      const myAgentId = data?.my_agent?.user_id || (user?.id ? Number(user.id) : 999);

      form.setFieldsValue({
        event: 'ringing',
        agent_id: myAgentId,
        extension: myExt,
        phone: '09123456789',
        direction: 'inbound',
        call_id: initialCallId,
        uniqueid: `fake_${initialCallId}`,
        queue: data?.queues?.[0]?.name || '',
        duration: 0,
        billsec: 0,
        disposition: 'ANSWERED',
        lookup_crm_match: true,
        persist_to_database: false,
        broadcast_to_all: Boolean(data?.broadcast_popup_to_all),
        audio_url: '',
      });
    } catch (err) {
      console.error('Failed to load fake call options:', err);
      // Fallback defaults
      const fallbackCallId = generateRandomCallId();
      form.setFieldsValue({
        event: 'ringing',
        agent_id: user?.id ? Number(user.id) : 999,
        extension: callCenter?.myAgentExtension || '999',
        phone: '09123456789',
        direction: 'inbound',
        call_id: fallbackCallId,
        uniqueid: `fake_${fallbackCallId}`,
        duration: 0,
        billsec: 0,
        disposition: 'ANSWERED',
        lookup_crm_match: true,
        persist_to_database: false,
        broadcast_to_all: false,
      });
    } finally {
      // Done loading options
    }
  }, [callCenter?.myAgentExtension, form, generateRandomCallId, user?.id]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  // Socket listener to verify karmadws broadcasts in real-time
  useSocketEvent('live_call_event', (payload) => {
    setLiveSocketLogs((prev) => [
      {
        id: Date.now() + Math.random(),
        time: new Date().toLocaleTimeString('fa-IR'),
        type: 'live_call_event',
        event: payload?.event,
        call_id: payload?.data?.call_id,
        phone: payload?.data?.phone,
        extension: payload?.extension || payload?.data?.extension,
        data: payload,
      },
      ...prev.slice(0, 19),
    ]);
  });

  useSocketEvent('call_event', (payload) => {
    setLiveSocketLogs((prev) => [
      {
        id: Date.now() + Math.random(),
        time: new Date().toLocaleTimeString('fa-IR'),
        type: 'call_event',
        event: payload?.event,
        call_id: payload?.call_id || payload?.call?.id,
        phone: payload?.phone,
        extension: payload?.extension,
        data: payload,
      },
      ...prev.slice(0, 19),
    ]);
  });

  // Watch current form values for Artisan Command Preview
  const [currentValues, setCurrentValues] = useState({});
  const handleValuesChange = (_, allValues) => {
    setCurrentValues(allValues);
    if (allValues.event && allValues.event !== selectedEventType) {
      setSelectedEventType(allValues.event);
    }
  };

  // Helper: Apply quick preset
  const applyPreset = (presetKey) => {
    switch (presetKey) {
      case 'ringing':
        setSelectedEventType('ringing');
        form.setFieldsValue({
          event: 'ringing',
          direction: 'inbound',
          duration: 0,
          billsec: 0,
          disposition: 'ANSWERED',
        });
        message.info('سناریوی تماس ورودی (Ringing) تنظیم شد');
        break;

      case 'answered':
        setSelectedEventType('answered');
        form.setFieldsValue({
          event: 'answered',
          direction: 'inbound',
          duration: 12,
          billsec: 12,
          disposition: 'ANSWERED',
        });
        message.info('سناریوی آغاز مکالمه (Answered / Talking) تنظیم شد');
        break;

      case 'hangup':
        setSelectedEventType('hangup');
        form.setFieldsValue({
          event: 'hangup',
          duration: 45,
          billsec: 35,
          disposition: 'ANSWERED',
        });
        message.info('سناریوی پایان تماس (Hangup) تنظیم شد');
        break;

      case 'missed':
        setSelectedEventType('hangup');
        form.setFieldsValue({
          event: 'hangup',
          direction: 'inbound',
          duration: 15,
          billsec: 0,
          disposition: 'NO ANSWER',
        });
        message.info('سناریوی تماس بی‌پاسخ (Missed) تنظیم شد');
        break;

      case 'outbound':
        setSelectedEventType('ringing');
        form.setFieldsValue({
          event: 'ringing',
          direction: 'outbound',
          duration: 0,
          billsec: 0,
          disposition: 'ANSWERED',
        });
        message.info('سناریوی تماس خروجی (Outbound) تنظیم شد');
        break;

      default:
        break;
    }
  };

  // Set to Logged-in User Info
  const setMyAgentInfo = () => {
    const myExt = callCenter?.myAgentExtension || meta?.my_agent?.extension || '999';
    const myId = meta?.my_agent?.user_id || (user?.id ? Number(user.id) : 999);
    form.setFieldsValue({
      agent_id: myId,
      extension: myExt,
    });
    message.success(`تنظیم شد: اپراتور #${myId} با داخلی ${myExt}`);
  };

  // Regenerate random IDs
  const handleRandomizeIds = () => {
    const newId = generateRandomCallId();
    form.setFieldsValue({
      call_id: newId,
      uniqueid: `fake_${newId}`,
    });
    message.success(`شناسه جدید تولید شد: ${newId}`);
  };

  // Submit fake call event
  const handlePublish = async (overrideValues = null) => {
    setLoading(true);
    try {
      const values = overrideValues || (await form.validateFields());
      const effectiveEvent = values.event === 'custom' ? customEvent : values.event;

      // Ensure call_id is unique across different phone numbers
      let callId = values.call_id;
      let uniqueId = values.uniqueid;
      const isReused = publishHistory.some(
        (h) => String(h.call_id) === String(callId) && String(h.phone) !== String(values.phone)
      );
      if (!callId || isReused) {
        callId = generateRandomCallId();
        uniqueId = `fake_${callId}`;
      }

      let payload = {
        ...values,
        call_id: callId,
        uniqueid: uniqueId,
        event: effectiveEvent,
      };

      // If custom JSON mode enabled, merge
      if (customJsonMode && customJsonText.trim()) {
        try {
          const parsed = JSON.parse(customJsonText);
          payload.custom_data = parsed;
        } catch (jsonErr) {
          message.error('فرمت JSON سفارشی نامعتبر است: ' + jsonErr.message);
          setLoading(false);
          return;
        }
      }

      const res = await callCenterFakeCallApi.publish(payload);
      const data = res?.data || {};

      message.success(res?.message || 'رویداد ساختگی تماس با موفقیت به ردیس و سوکت ارسال شد');

      // Automatically generate a fresh unique ID for the next call in the form
      const nextCallId = generateRandomCallId();
      form.setFieldsValue({
        call_id: nextCallId,
        uniqueid: `fake_${nextCallId}`,
      });

      // Append to local history
      setPublishHistory((prev) => [
        {
          id: Date.now() + Math.random(),
          timestamp: new Date().toLocaleTimeString('fa-IR'),
          event: effectiveEvent,
          agent_id: values.agent_id,
          extension: values.extension,
          phone: values.phone,
          call_id: data?.call_id || callId,
          uniqueid: data?.uniqueid || uniqueId,
          direction: values.direction,
          persisted: Boolean(data?.persisted),
          payload: data?.data || payload,
        },
        ...prev,
      ]);

      return data;
    } catch (err) {
      console.error('Publish fake call failed:', err);
      message.error(err?.message || 'خطا در ارسال رویداد ساختگی تماس');
    } finally {
      setLoading(false);
    }
  };

  // Stop auto simulation sequence
  const stopSequence = useCallback(() => {
    if (sequenceRef.current.timerId) {
      clearTimeout(sequenceRef.current.timerId);
    }
    sequenceRef.current = { active: false, callId: null, timerId: null };
    setSequenceActive(false);
    setSequenceStep(0);
    message.info('شبیه‌سازی خودکار متوقف شد.');
  }, []);

  // Run full sequential simulation: Ringing -> Answered -> Hangup
  const startFullSequence = async () => {
    if (sequenceActive) {
      stopSequence();
      return;
    }

    try {
      const baseValues = await form.validateFields();
      const callId = baseValues.call_id || generateRandomCallId();
      const uniqueId = `fake_${callId}`;

      form.setFieldsValue({ call_id: callId, uniqueid: uniqueId });
      sequenceRef.current = { active: true, callId, timerId: null };
      setSequenceActive(true);
      setSequenceStep(1);

      message.loading({ content: 'مرحله ۱ از ۳: ارسال زنگ تماس (Ringing / Popup)...', key: 'seq' });

      // Step 1: Ringing
      await handlePublish({
        ...baseValues,
        event: 'ringing',
        call_id: callId,
        uniqueid: uniqueId,
        duration: 0,
        billsec: 0,
      });

      // Step 2: Answered after 3.5 seconds
      sequenceRef.current.timerId = setTimeout(async () => {
        if (!sequenceRef.current.active) return;
        setSequenceStep(2);
        message.loading({ content: 'مرحله ۲ از ۳: پاسخ تماس (Answered / Talking)...', key: 'seq' });

        await handlePublish({
          ...baseValues,
          event: 'answered',
          call_id: callId,
          uniqueid: uniqueId,
          duration: 5,
          billsec: 5,
        });

        // Step 3: Hangup after 5 seconds
        sequenceRef.current.timerId = setTimeout(async () => {
          if (!sequenceRef.current.active) return;
          setSequenceStep(3);
          message.loading({ content: 'مرحله ۳ از ۳: پایان مکالمه (Hangup)...', key: 'seq' });

          await handlePublish({
            ...baseValues,
            event: 'hangup',
            call_id: callId,
            uniqueid: uniqueId,
            duration: 25,
            billsec: 20,
            disposition: 'ANSWERED',
          });

          message.success({ content: 'شبیه‌سازی سناریوی کامل تماس با موفقیت به پایان رسید!', key: 'seq', duration: 3 });
          setSequenceActive(false);
          setSequenceStep(0);
          sequenceRef.current.active = false;
        }, 5000);
      }, 3500);
    } catch (err) {
      console.error('Sequence simulation error:', err);
      stopSequence();
    }
  };

  // Generate equivalent Artisan CLI command string
  const artisanCommandString = useMemo(() => {
    const fValues = { ...form.getFieldsValue(), ...currentValues };
    const evt = fValues.event === 'custom' ? (customEvent || 'ringing') : (fValues.event || 'ringing');
    const parts = [`php artisan callcenter:fake-call ${evt}`];

    if (fValues.agent_id !== undefined && fValues.agent_id !== null && fValues.agent_id !== '') {
      parts.push(`--agent=${fValues.agent_id}`);
    }
    if (fValues.extension) {
      parts.push(`--extension=${fValues.extension}`);
    }
    if (fValues.phone) {
      parts.push(`--phone=${fValues.phone}`);
    }
    if (fValues.direction && fValues.direction !== 'inbound') {
      parts.push(`--direction=${fValues.direction}`);
    }
    if (fValues.call_id) {
      parts.push(`--call-id=${fValues.call_id}`);
    }
    if (fValues.uniqueid) {
      parts.push(`--uniqueid=${fValues.uniqueid}`);
    }
    if (fValues.duration) {
      parts.push(`--duration=${fValues.duration}`);
    }
    if (fValues.billsec) {
      parts.push(`--billsec=${fValues.billsec}`);
    }
    if (fValues.disposition) {
      parts.push(`--disposition=${fValues.disposition}`);
    }
    if (fValues.queue) {
      parts.push(`--queue=${fValues.queue}`);
    }
    if (fValues.audio_url) {
      parts.push(`--audio-url="${fValues.audio_url}"`);
    }
    if (fValues.persist_to_database) {
      parts.push('--persist');
    }
    if (fValues.broadcast_to_all) {
      parts.push('--broadcast');
    }

    return parts.join(' ');
  }, [currentValues, customEvent, form]);

  const handleCopyCommand = async () => {
    try {
      await navigator.clipboard.writeText(artisanCommandString);
      message.success('دستور Artisan در کلیپ‌بورد کپی شد');
    } catch {
      message.error('خطا در کپی کردن دستور');
    }
  };

  return (
    <MainLayout
      title="شبیه‌ساز تماس (Fake Call Generator)"
      activeMenu="/call-center/fake-call"
      breadcrumbs={[
        { title: 'مرکز تماس', path: '/call-center/calls' },
        { title: 'شبیه‌ساز تماس' },
      ]}
    >
      <div style={{ fontFamily: 'CustomFont' }}>
        <PermissionGate module="callcenter" permission="view_callcenter" showWarning>
          {/* Standard Outer Container Pattern */}
          <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }} styles={{ body: { padding: 0 } }}>
            {/* Header Bar */}
            <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white flex items-center justify-center shadow-lg">
                  <ThunderboltOutlined className="text-xl" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0 custom-font">
                      شبیه‌ساز تماس‌های مرکز تماس
                    </h1>
                    <Tag color="cyan" className="custom-font">
                      PublishFakeCall
                    </Tag>
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm m-0 custom-font">
                    تولید و انتشار رویدادهای ساختگی تماس در ردیس و سرور وب‌سوکت (KarmaDWS) جهت تست پاپ‌آپ Screen Pop، مانیتور زنده و تطبیق CRM
                  </p>
                </div>
              </div>

              {/* Status Tags & Quick Actions */}
              <div className="flex items-center gap-2 flex-wrap">
                <Tag
                  color={isConnected ? 'success' : 'error'}
                  className="custom-font px-3 py-1 text-sm rounded-lg flex items-center gap-1.5"
                >
                  <span
                    className={`inline-block w-2 h-2 rounded-full ${
                      isConnected ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'
                    }`}
                  />
                  {isConnected ? 'متصل به وب‌سوکت (KarmaDWS)' : 'سوکت قطع است'}
                </Tag>

                {callCenter?.myAgentExtension && (
                  <Tag color="purple" className="custom-font px-3 py-1 text-sm rounded-lg">
                    داخلی من: <span className="font-bold">{callCenter.myAgentExtension}</span>
                  </Tag>
                )}

                <Button
                  icon={<UserOutlined />}
                  size="small"
                  onClick={setMyAgentInfo}
                  className="custom-font text-xs rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 hover:dark:border-teal-400 hover:dark:text-teal-400"
                >
                  تنظیم به مشخصات من
                </Button>
              </div>
            </div>

            {/* Content Body Area */}
            <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl space-y-6">
              {/* Quick Preset Action Bar */}
              <Card
                bordered={false}
                className="shadow-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
              >
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <PlayCircleOutlined className="text-teal-600 dark:text-teal-400 text-lg" />
                      <span className="font-bold text-gray-800 dark:text-gray-200 custom-font">
                        سناریوهای تست سریع (با یک کلیک):
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      {sequenceActive && (
                        <Tag color="processing" icon={<SyncOutlined spin />} className="custom-font">
                          مرحله {sequenceStep} از ۳ در حال اجرا...
                        </Tag>
                      )}
                      <Button
                        type={sequenceActive ? 'default' : 'dashed'}
                        danger={sequenceActive}
                        icon={sequenceActive ? <StopOutlined /> : <ThunderboltOutlined />}
                        onClick={startFullSequence}
                        className="custom-font rounded-xl font-medium dark:bg-gray-700/60 dark:border-gray-600 dark:text-teal-300 hover:dark:border-teal-400"
                      >
                        {sequenceActive ? 'توقف شبیه‌سازی زنجیره‌ای' : '⚡ اجرای سناریوی کامل خودکار (Ring ➔ Answer ➔ Hangup)'}
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      icon={<PhoneOutlined className="text-emerald-500" />}
                      onClick={() => applyPreset('ringing')}
                      className="custom-font rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:border-emerald-400 dark:hover:border-emerald-600"
                    >
                      ۱. تماس ورودی زنگ‌خور (Ringing / Popup)
                    </Button>

                    <Button
                      icon={<PlayCircleOutlined className="text-blue-500" />}
                      onClick={() => applyPreset('answered')}
                      className="custom-font rounded-xl bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 hover:border-blue-400 dark:hover:border-blue-600"
                    >
                      ۲. آغاز مکالمه (Answered / Talking)
                    </Button>

                    <Button
                      icon={<StopOutlined className="text-rose-500" />}
                      onClick={() => applyPreset('hangup')}
                      className="custom-font rounded-xl bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 hover:border-rose-400 dark:hover:border-rose-600"
                    >
                      ۳. پایان تماس (Hangup)
                    </Button>

                    <Button
                      icon={<CloseCircleOutlined className="text-amber-500" />}
                      onClick={() => applyPreset('missed')}
                      className="custom-font rounded-xl bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 hover:border-amber-400 dark:hover:border-amber-600"
                    >
                      تماس بی‌پاسخ (Missed)
                    </Button>

                    <Button
                      icon={<SendOutlined className="text-violet-500" />}
                      onClick={() => applyPreset('outbound')}
                      className="custom-font rounded-xl bg-violet-50 dark:bg-violet-950/40 border-violet-200 dark:border-violet-800 text-violet-700 dark:text-violet-300 hover:border-violet-400 dark:hover:border-violet-600"
                    >
                      تماس خروجی (Outbound)
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Main Form & Configuration */}
              <Row gutter={[20, 20]}>
                <Col xs={24} lg={15}>
                  <Card
                    bordered={false}
                    title={
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-gray-800 dark:text-gray-100 custom-font">
                          تنظیم پارامترهای تماس ساختگی
                        </span>
                        <Button
                          size="small"
                          icon={<ReloadOutlined />}
                          onClick={handleRandomizeIds}
                          className="custom-font text-xs rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 hover:dark:border-teal-400 hover:dark:text-teal-400"
                        >
                          تولید شناسه تصادفی جدید
                        </Button>
                      </div>
                    }
                    className="shadow-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                  >
                    <Form
                      form={form}
                      layout="vertical"
                      onValuesChange={handleValuesChange}
                      initialValues={{
                        event: 'ringing',
                        direction: 'inbound',
                        disposition: 'ANSWERED',
                        lookup_crm_match: true,
                        persist_to_database: false,
                      }}
                    >
                      {/* Row 1: Event & Direction */}
                      <Row gutter={16}>
                        <Col xs={24} md={14}>
                          <Form.Item
                            name="event"
                            label="نوع رویداد (Event)"
                            rules={[{ required: true, message: 'انتخاب نوع رویداد الزامی است' }]}
                          >
                            <Select
                              className="custom-font"
                              options={DEFAULT_EVENT_OPTIONS}
                              onChange={(val) => setSelectedEventType(val)}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={10}>
                          <Form.Item
                            name="direction"
                            label="جهت تماس (Direction)"
                            rules={[{ required: true }]}
                          >
                            <Radio.Group buttonStyle="solid" className="w-full flex">
                              <Radio.Button value="inbound" className="flex-1 text-center custom-font">
                                ورودی (Inbound)
                              </Radio.Button>
                              <Radio.Button value="outbound" className="flex-1 text-center custom-font">
                                خروجی (Outbound)
                              </Radio.Button>
                            </Radio.Group>
                          </Form.Item>
                        </Col>
                      </Row>

                      {/* Custom Event Text Input */}
                      {selectedEventType === 'custom' && (
                        <Row gutter={16}>
                          <Col span={24}>
                            <Form.Item label="نام رویداد سفارشی">
                              <Input
                                placeholder="مثال: custom_event_name"
                                value={customEvent}
                                onChange={(e) => setCustomEvent(e.target.value)}
                                dir="ltr"
                                className="custom-font"
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}

                      {/* Row 2: Target Agent & Extension */}
                      <Row gutter={16}>
                        <Col xs={24} md={14}>
                          <Form.Item
                            name="agent_id"
                            label={
                              <div className="flex items-center justify-between w-full">
                                <span>اپراتور هدف (Target Agent)</span>
                                <button
                                  type="button"
                                  onClick={setMyAgentInfo}
                                  className="text-xs text-teal-600 dark:text-teal-400 hover:underline cursor-pointer bg-transparent border-0 p-0 custom-font"
                                >
                                  [تنظیم به من]
                                </button>
                              </div>
                            }
                            tooltip="شناسه کاربری اپراتور در ERP. رویداد به اتاق اختصاصی این کاربر در سوکت ارسال می‌شود."
                          >
                            <Select
                              showSearch
                              placeholder="انتخاب اپراتور یا وارد کردن شناسه"
                              className="custom-font"
                              filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                              }
                              options={[
                                ...(meta?.agents || []).map((a) => ({
                                  value: Number(a.user_id),
                                  label: `${a.name} (داخلی: ${a.extension} | شناسه: #${a.user_id})`,
                                })),
                                { value: 999, label: 'اپراتور تستی پیش‌فرض (#999)' },
                              ]}
                              onChange={(val) => {
                                const found = meta?.agents?.find((a) => Number(a.user_id) === Number(val));
                                if (found?.extension) {
                                  form.setFieldsValue({ extension: found.extension });
                                }
                              }}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={10}>
                          <Form.Item
                            name="extension"
                            label="شماره داخلی اپراتور (Extension)"
                            rules={[{ required: true, message: 'شماره داخلی الزامی است' }]}
                            tooltip="داخلی سانترال اپراتور (مثلاً 101). رویداد به اتاق ext_[داخلی] نیز ارسال می‌شود."
                          >
                            <Input placeholder="مثال: 101 یا 999" dir="ltr" className="custom-font" />
                          </Form.Item>
                        </Col>
                      </Row>

                      {/* Row 3: Phone Number & Sample CRM Contacts */}
                      <Row gutter={16}>
                        <Col span={24}>
                          <Form.Item
                            name="phone"
                            label="شماره تماس‌گیرنده / مخاطب (Phone)"
                            rules={[{ required: true, message: 'شماره تلفن الزامی است' }]}
                          >
                            <Input
                              placeholder="09123456789"
                              dir="ltr"
                              className="custom-font font-mono text-base"
                              prefix={<PhoneOutlined className="text-gray-400 dark:text-gray-500" />}
                            />
                          </Form.Item>

                          {/* Quick CRM Contact Chips */}
                          {meta?.sample_contacts?.length > 0 && (
                            <div className="mb-4 bg-gray-50 dark:bg-gray-900/60 p-3 rounded-xl border border-gray-200 dark:border-gray-700">
                              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 custom-font">
                                تست تطبیق با مخاطبان واقعی سیستم (CRM Match):
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {meta.sample_contacts.map((c) => (
                                  <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => {
                                      form.setFieldsValue({ phone: c.mobile });
                                      message.info(`شماره مخاطب ${c.name} (${c.mobile}) تنظیم شد`);
                                    }}
                                    className="text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 hover:border-teal-400 hover:text-teal-600 dark:hover:text-teal-400 px-2.5 py-1.5 rounded-lg transition-all custom-font flex items-center gap-1.5"
                                  >
                                    <UserOutlined className="text-[10px] text-teal-600 dark:text-teal-400" />
                                    <span>{c.name}</span>
                                    <span className="text-gray-400 dark:text-gray-500 font-mono" dir="ltr">
                                      ({c.mobile})
                                    </span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </Col>
                      </Row>

                      {/* Row 4: Call ID & Unique ID */}
                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <Form.Item
                            name="call_id"
                            label="شناسه تماس (Call ID)"
                            tooltip="شناسه عددی تماس در سیستم"
                          >
                            <InputNumber className="w-full custom-font font-mono" placeholder="4521" />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                          <Form.Item
                            name="uniqueid"
                            label="شناسه یکتا PBX (Unique ID)"
                            tooltip="شناسه یکتای سانترال استریسک یا سیموتل"
                          >
                            <Input dir="ltr" className="custom-font font-mono" placeholder="fake_4521" />
                          </Form.Item>
                        </Col>
                      </Row>

                      {/* Row 5: Queue & Disposition */}
                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <Form.Item name="queue" label="صف تماس (Queue)">
                            <Select
                              allowClear
                              placeholder="انتخاب صف (اختیاری)"
                              className="custom-font"
                              options={[
                                ...(meta?.queues || []).map((q) => ({
                                  value: q.name,
                                  label: `${q.title || q.name} (${q.name})`,
                                })),
                                { value: 'Support', label: 'Support' },
                                { value: 'Sales', label: 'Sales' },
                              ]}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                          <Form.Item name="disposition" label="وضعیت پاسخ‌گویی (Disposition)">
                            <Select
                              className="custom-font"
                              options={[
                                { value: 'ANSWERED', label: 'پاسخ‌داده‌شده (ANSWERED)' },
                                { value: 'NO ANSWER', label: 'بی‌پاسخ (NO ANSWER)' },
                                { value: 'BUSY', label: 'اشغال (BUSY)' },
                                { value: 'FAILED', label: 'ناموفق (FAILED)' },
                                { value: 'CANCELLED', label: 'لغوشده (CANCELLED)' },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      {/* Row 6: Duration & Billsec */}
                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <Form.Item
                            name="duration"
                            label="مدت کل تماس (ثانیه)"
                            tooltip="مدت زمان کل تماس از شروع تا قطع"
                          >
                            <InputNumber min={0} className="w-full custom-font" placeholder="45" />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                          <Form.Item
                            name="billsec"
                            label="مدت مکالمه مفید (ثانیه)"
                            tooltip="مدت زمان گفتگوی مفید پس از برداشتن گوشی"
                          >
                            <InputNumber min={0} className="w-full custom-font" placeholder="30" />
                          </Form.Item>
                        </Col>
                      </Row>

                      {/* Row 7: Audio URL (Optional) */}
                      <Row gutter={16}>
                        <Col span={24}>
                          <Form.Item
                            name="audio_url"
                            label="آدرس فایل صوتی ضبط‌شده (Audio Stream URL - اختیاری)"
                            tooltip="جهت تست پلیر فایل ضبط‌شده در تاریخچه تماس‌ها"
                          >
                            <Input
                              placeholder="https://example.com/recordings/fake_call.mp3"
                              dir="ltr"
                              className="custom-font"
                              prefix={<LinkOutlined className="text-gray-400 dark:text-gray-500" />}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Divider className="my-3 border-gray-200 dark:border-gray-700" />

                      {/* Advanced Toggles */}
                      <Row gutter={16} className="mb-4">
                        <Col xs={24} md={8}>
                          <Form.Item
                            name="lookup_crm_match"
                            valuePropName="checked"
                            className="m-0"
                          >
                            <div className="flex items-center justify-between p-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60">
                              <div>
                                <div className="font-medium text-gray-800 dark:text-gray-100 text-sm custom-font">
                                  تطبیق خودکار با CRM
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 custom-font">
                                  واکشی نام و پرونده مشتری/سرنخ بر اساس شماره
                                </div>
                              </div>
                              <Switch defaultChecked />
                            </div>
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                          <Form.Item
                            name="broadcast_to_all"
                            valuePropName="checked"
                            className="m-0"
                          >
                            <div className="flex items-center justify-between p-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60">
                              <div>
                                <div className="font-medium text-gray-800 dark:text-gray-100 text-sm custom-font">
                                  نمایش به تمام اپراتورها
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 custom-font">
                                  ارسال پاپ‌آپ برای کلیه اپراتورهای همین شرکت
                                </div>
                              </div>
                              <Switch />
                            </div>
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                          <Form.Item
                            name="persist_to_database"
                            valuePropName="checked"
                            className="m-0"
                          >
                            <div className="flex items-center justify-between p-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60">
                              <div>
                                <div className="font-medium text-gray-800 dark:text-gray-100 text-sm custom-font">
                                  ذخیره در دیتابیس (Calls Table)
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 custom-font">
                                  ثبت در جدول تاریخچه تماس‌های مرکز تماس
                                </div>
                              </div>
                              <Switch />
                            </div>
                          </Form.Item>
                        </Col>
                      </Row>

                      {/* Custom JSON Payload Collapse */}
                      <div className="mb-6">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400 custom-font">
                            امکان تزریق یا بازنویسی داده‌های سفارشی JSON (Custom Payload):
                          </span>
                          <Switch
                            size="small"
                            checked={customJsonMode}
                            onChange={setCustomJsonMode}
                            checkedChildren="فعال"
                            unCheckedChildren="غیرفعال"
                          />
                        </div>

                        {customJsonMode && (
                          <Input.TextArea
                            rows={4}
                            dir="ltr"
                            className="font-mono text-xs custom-font dark:bg-gray-900/80 dark:border-gray-700 dark:text-gray-100"
                            placeholder={'{\n  "custom_field": "test_value"\n}'}
                            value={customJsonText}
                            onChange={(e) => setCustomJsonText(e.target.value)}
                          />
                        )}
                      </div>

                      {/* Main Action Submit Button */}
                      <div className="flex items-center justify-end gap-3 pt-2">
                        <Button
                          type="primary"
                          size="large"
                          icon={<SendOutlined />}
                          loading={loading}
                          onClick={() => handlePublish()}
                          className={`custom-font font-bold px-8 h-12 rounded-xl text-base ${PANEL_PRIMARY_BTN_CLASS}`}
                        >
                          🚀 ارسال رویداد ساختگی تماس (Publish Event)
                        </Button>
                      </div>
                    </Form>
                  </Card>
                </Col>

                {/* Sidebar Right Column: CLI Preview, History & Live Socket Monitor */}
                <Col xs={24} lg={9} className="space-y-6">
                  {/* Equivalent Artisan Command Card */}
                  <Card
                    bordered={false}
                    title={
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-gray-800 dark:text-gray-100 text-sm custom-font flex items-center gap-1.5">
                          <CodeOutlined className="text-teal-600 dark:text-teal-400" />
                          دستور معادل Artisan در CLI
                        </span>
                        <Button
                          size="small"
                          type="text"
                          icon={<CopyOutlined />}
                          onClick={handleCopyCommand}
                          className="custom-font text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300"
                        >
                          کپی دستور
                        </Button>
                      </div>
                    }
                    className="shadow-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                  >
                    <div className="bg-gray-900 text-emerald-400 p-3.5 rounded-xl font-mono text-xs overflow-x-auto whitespace-pre-wrap break-all select-all border border-gray-800">
                      {artisanCommandString}
                    </div>
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 custom-font">
                      دقیقاً می‌توانید این دستور را در محیط سرور یا ترمینال پروژه backend-erp اجرا نمایید.
                    </div>
                  </Card>

                  {/* Tabs for Live Socket Events & Session Publish History */}
                  <Card
                    bordered={false}
                    className="shadow-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                  >
                    <Tabs
                      defaultActiveKey="live"
                      items={[
                        {
                          key: 'live',
                          label: (
                            <span className="custom-font flex items-center gap-1.5 text-gray-700 dark:text-gray-200">
                              <ThunderboltOutlined className="text-teal-500" />
                              ردیاب زنده سوکت ({liveSocketLogs.length})
                            </span>
                          ),
                          children: (
                            <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
                              {liveSocketLogs.length === 0 ? (
                                <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-xs custom-font">
                                  هنوز رویدادی از سرور سوکت دریافت نشده است.
                                  <br />
                                  پس از ارسال، رویداد به‌صورت زنده در اینجا ثبت می‌شود.
                                </div>
                              ) : (
                                liveSocketLogs.map((log) => (
                                  <div
                                    key={log.id}
                                    className="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 text-xs flex flex-col gap-1.5 transition-all hover:border-teal-400 dark:hover:border-teal-500"
                                  >
                                    <div className="flex items-center justify-between">
                                      <Tag color={log.event === 'hangup' ? 'red' : 'green'} className="custom-font m-0">
                                        {log.event}
                                      </Tag>
                                      <span className="text-gray-400 dark:text-gray-500 text-[11px] font-mono">{log.time}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-gray-700 dark:text-gray-300">
                                      <span>داخلی: {log.extension || '—'}</span>
                                      <span className="font-mono text-gray-800 dark:text-gray-200 font-semibold" dir="ltr">
                                        {log.phone || '—'}
                                      </span>
                                    </div>
                                    <div className="text-gray-400 dark:text-gray-500 text-[10px] font-mono">شناسه: {log.call_id || '—'}</div>
                                  </div>
                                ))
                              )}
                            </div>
                          ),
                        },
                        {
                          key: 'history',
                          label: (
                            <span className="custom-font flex items-center gap-1.5 text-gray-700 dark:text-gray-200">
                              <HistoryOutlined />
                              ارسال‌های این جلسه ({publishHistory.length})
                            </span>
                          ),
                          children: (
                            <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
                              {publishHistory.length === 0 ? (
                                <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-xs custom-font">
                                  در این نشست هنوز رویدادی ارسال نشده است.
                                </div>
                              ) : (
                                publishHistory.map((item) => (
                                  <div
                                    key={item.id}
                                    className="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 text-xs flex flex-col gap-1.5"
                                  >
                                    <div className="flex items-center justify-between">
                                      <Tag
                                        color={item.event === 'hangup' ? 'volcano' : 'cyan'}
                                        className="custom-font m-0"
                                      >
                                        {item.event}
                                      </Tag>
                                      <span className="text-gray-400 dark:text-gray-500 text-[11px] font-mono">{item.timestamp}</span>
                                    </div>

                                    <div className="flex items-center justify-between text-gray-700 dark:text-gray-300">
                                      <span>اپراتور: {item.agent_id} (داخلی {item.extension})</span>
                                      <span className="font-mono text-gray-800 dark:text-gray-200 font-semibold" dir="ltr">
                                        {item.phone}
                                      </span>
                                    </div>

                                    <div className="flex items-center justify-between pt-1.5 border-t border-gray-200 dark:border-gray-700">
                                      <span className="text-gray-400 dark:text-gray-500 text-[10px] font-mono">
                                        Call ID: {item.call_id}
                                      </span>
                                      <div className="flex items-center gap-1.5">
                                        {item.persisted && (
                                          <Button
                                            type="link"
                                            size="small"
                                            className="p-0 text-[11px] custom-font text-blue-500 dark:text-blue-400 hover:underline"
                                            onClick={() => navigate(`/call-center/calls/${item.call_id}`)}
                                          >
                                            مشاهده تماس
                                          </Button>
                                        )}
                                        <Button
                                          type="link"
                                          size="small"
                                          className="p-0 text-[11px] custom-font text-teal-600 dark:text-teal-400 hover:underline"
                                          onClick={() => handlePublish(item.payload)}
                                        >
                                          ارسال مجدد
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          ),
                        },
                      ]}
                    />
                  </Card>

                  {/* Quick Links */}
                  <Card
                    bordered={false}
                    size="small"
                    className="shadow-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                  >
                    <div className="text-xs font-bold text-gray-700 dark:text-gray-200 mb-2 custom-font">
                      بخش‌های مرتبط جهت بررسی نتیجه:
                    </div>
                    <div className="flex flex-col gap-1.5 text-xs">
                      <a
                        href="/call-center/live"
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/60 text-gray-600 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors custom-font"
                      >
                        <span>مرکز تماس زنده (Live Monitor)</span>
                        <LinkOutlined />
                      </a>
                      <a
                        href="/call-center/calls"
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/60 text-gray-600 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors custom-font"
                      >
                        <span>تاریخچه تماس‌ها (Call History)</span>
                        <LinkOutlined />
                      </a>
                    </div>
                  </Card>
                </Col>
              </Row>
            </div>
          </Card>
        </PermissionGate>
      </div>
    </MainLayout>
  );
};

export default CallCenterFakeCall;
